// /app/frontend/src/components/frugal-v2/hooks/useZenLock.js
// Zen Lock hook - inactivity detection and lock state (120 lines)

import { useState, useEffect, useCallback, useRef } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

export function useZenLock(token) {
  const [isLocked, setIsLocked] = useState(false);
  const [config, setConfig] = useState({ enabled: false, timeout_minutes: 5, has_pin: false });
  const [loading, setLoading] = useState(true);
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Fetch config from backend
  const fetchConfig = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API}/api/v2/frugal/settings/zen-lock`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('[ZenLock] Config loaded:', data);
        setConfig(data);
      }
    } catch (error) {
      console.error('Error fetching Zen Lock config:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Update config
  const updateConfig = useCallback(async (newConfig) => {
    if (!token) return false;
    try {
      const response = await fetch(`${API}/api/v2/frugal/settings/zen-lock`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newConfig)
      });
      if (response.ok) {
        await fetchConfig();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating Zen Lock config:', error);
      return false;
    }
  }, [token, fetchConfig]);

  // Verify PIN
  const verifyPin = useCallback(async (pin) => {
    if (!token) return false;
    try {
      const response = await fetch(`${API}/api/v2/frugal/settings/zen-lock/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ pin })
      });
      if (response.ok) {
        setIsLocked(false);
        lastActivityRef.current = Date.now();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error verifying PIN:', error);
      return false;
    }
  }, [token]);

  // Inactivity timer effect
  useEffect(() => {
    console.log('[ZenLock] Effect running:', { enabled: config.enabled, has_pin: config.has_pin, isLocked, timeout: config.timeout_minutes });
    
    if (!config.enabled || !config.has_pin || isLocked) {
      console.log('[ZenLock] Timer not started - conditions not met');
      return;
    }

    const timeoutMs = config.timeout_minutes * 60 * 1000;
    console.log('[ZenLock] Starting timer for', timeoutMs, 'ms (', config.timeout_minutes, 'min)');
    
    const checkInactivity = () => {
      const now = Date.now();
      const elapsed = now - lastActivityRef.current;
      
      if (elapsed >= timeoutMs) {
        console.log('[ZenLock] Locking due to inactivity');
        setIsLocked(true);
      }
    };

    const handleActivity = () => {
      lastActivityRef.current = Date.now();
    };

    // Reset activity time
    lastActivityRef.current = Date.now();

    // Check every 10 seconds instead of using setTimeout
    const intervalId = setInterval(checkInactivity, 10000);

    // Listen for activity
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(event => window.addEventListener(event, handleActivity, { passive: true }));

    return () => {
      clearInterval(intervalId);
      events.forEach(event => window.removeEventListener(event, handleActivity));
    };
  }, [config.enabled, config.has_pin, config.timeout_minutes, isLocked]);

  // Fetch config on mount
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Manual lock
  const lock = useCallback(() => {
    if (config.enabled && config.has_pin) {
      setIsLocked(true);
    }
  }, [config]);

  return {
    isLocked,
    config,
    loading,
    updateConfig,
    verifyPin,
    lock,
    refetchConfig: fetchConfig
  };
}

export default useZenLock;
